import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";

import { makeStyles } from "tss-react/mui";
import { ThemeProvider } from "@emotion/react";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Toolbar from "@mui/material/Toolbar";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

import KMKTable from "../../Common/Table/KMKTable";
import ReportService from "../../../service/report";
import MultiSelectDropDown from "../../Common/MultiSelectDropDown";
import MultiSelect from "../../../util/multiselect";
import useWindowResize from "../../../hooks/useWindowsReszie";
import { createTheme } from "../../MyWorkspace/Detail/overview/theme";
import Workspace from "../../../service/workspace";
import { convertTimezone } from "../../../util/common";

const statusList = [
  { Name: "Ready", label: "ready" },
  { Name: "Running", label: "running" },
  { Name: "Success", label: "success" },
  { Name: "Failed", label: "error" },
  { Name: "Warning", label: "warning" },
  { Name: "Terminating", label: "terminating" },
  { Name: "Terminated", label: "terminated" },
];

const defaultStatusFilter = ["running", "terminating"];

const useStyles = makeStyles()((theme) => ({
  Header: {
    fontSize: "1.1rem",
    backgroundColor: "#e8edf1",
    color: "black",
    margin: 0,
    borderRadius: 0,
    boxShadow: 0,
    fontWeight: 600,
    display: "flex",
    flexWrap: "wrap",
    padding: "0px !important",
  },
  HeaderTitle: {
    fontSize: 20,
    display: "inline-flex",

    "& span": {
      marginLeft: "5px",
    },
  },
  DataTableContainer: {
    borderRadius: 10,
    backgroundColor: "#FFFFFF",
    padding: "5px 0px 0px",
    overflowX: "auto",
    width: "100%",
    overflow: "hidden",
    "& .MuiTableRow-head .MuiTableCell-root": {
      padding: "6px 16px",
      height: "37px",
    },
    "& .MuiTableBody-root .MuiTableCell-root": {
      padding: "6px 16px",
      borderBottom: "none",
      wordBreak: "break-word",
    },
    "& .MuiTablePagination-toolbar": {
      minHeight: "40px",
    },
    "& P.MuiTablePagination-selectLabel,P.MuiTablePagination-displayedRows": {
      marginBottom: "0px !important",
    },
    "& ::-webkit-scrollbar-track": {
      marginTop: "35px",
      marginLeft: "-2px",
    },
  },
  JobInfoTitle: { display: "flex", flex: "1", alignItems: "center", paddingRight: "16px" },
}));

const theme = createTheme();

const DetailTable = () => {
  const { classes } = useStyles();
  const { height: tableHeight } = useWindowResize(510);

  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [count, setCount] = useState(0);
  const [workspaceList, setWorkspaceList] = useState([]);
  const [runningJobs, setRunningJobs] = useState(0);
  const [queuedJobs, setQueuedJobs] = useState(0);
  const [scheduledJobs, setScheduledJobs] = useState(0);

  const options = useRef({
    limit: 10,
    page: 0,
    orderBy: "Name",
    order: "asc",
    selectedStatus: defaultStatusFilter,
    workspaces: [],
  });

  const getMetrics = useCallback(async () => {
    const sageMaker = new ReportService();
    const { success, data } = await sageMaker.getMetrics();

    if (success) {
      setRunningJobs(data.running || 0);
      setQueuedJobs(data.queue || 0);
      setScheduledJobs(data.scheduled || 0);
    }
  }, []);

  const getUsageReport = useCallback(async () => {
    const { order, orderBy, page, limit, selectedStatus, workspaces } = options.current;

    setLoading(true);
    setJobs([]);
    getMetrics();
    const sageMaker = new ReportService();
    const { success, data } = await sageMaker.getUsageReport({
      order,
      orderBy,
      page,
      limit,
      ...(selectedStatus.length === statusList.length ? {} : { statusIds: selectedStatus.join(",") }),
      ...(workspaces.length === workspaceList.length ? {} : { workspaceIds: workspaces.join(",") }),
    });
    if (success) {
      setJobs(data.jobs);
      setCount(data.count);
    }
    setLoading(false);
  }, [getMetrics, workspaceList.length]);

  useEffect(() => {
    getUsageReport();
  }, []);

  useEffect(() => {
    new Workspace().getAllWorkspaces().then((response) => {
      if (response.success) {
        options.current.workspaces = response.data.map(({ Workspace_id }) => Workspace_id);
        setWorkspaceList(response.data.map(({ Workspace_id, Name }) => ({ Name, label: Workspace_id })));
      }
    });
  }, []);

  const handleSorting = useCallback(
    (property) => {
      const isAsc = options.current.orderBy === property && options.current.order === "asc";

      options.current.orderBy = property;
      options.current.order = isAsc ? "desc" : "asc";

      getUsageReport();
    },
    [getUsageReport]
  );

  const reportColumns = useMemo(
    () => [
      {
        id: "Name",
        name: "JOB NAME",
        sortable: true,
        style: { maxWidth: 160, wordBreak: "break-word" },
        align: "left",
        width: 160,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => val || "-",
      },
      {
        id: "JobType",
        name: "JOB TYPE",
        sortable: true,
        style: { maxWidth: 120, wordBreak: "break-word" },
        align: "left",
        width: 120,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => val || "-",
      },
      {
        id: "DataPipelineName",
        name: "PIPELINE",
        sortable: true,
        style: { maxWidth: 120, wordBreak: "break-word" },
        align: "left",
        width: 120,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => val || "-",
      },
      {
        id: "WorkspaceName",
        name: "WORKSPACE",
        sortable: true,
        align: "left",
        width: 130,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => val || "-",
      },
      {
        id: "statusText",
        name: "STATUS",
        sortable: false,
        align: "left",
        width: 130,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => val || "-",
      },
      {
        id: "StartTime",
        name: "LAST RUN",
        sortable: true,
        align: "left",
        width: 140,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => (val ? convertTimezone(val) : "-"),
      },
      {
        id: "Schedule",
        name: `Next RUN`,
        sortable: true,
        align: "left",
        width: 140,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => record?.nextRunStartsIn || "-",
      },
    ],
    []
  );

  const handleChangePage = useCallback(
    (_event, newPage) => {
      options.current.page = newPage;

      getUsageReport();
    },
    [getUsageReport]
  );

  const handleChangeRowsPerPage = useCallback(
    (event) => {
      options.current.limit = parseInt(event.target.value, 10);
      options.current.page = 0;

      getUsageReport();
    },
    [getUsageReport]
  );

  const handleStatusChange = useCallback(
    (selectionList) => {
      options.current.selectedStatus = selectionList;

      getUsageReport();
    },
    [getUsageReport]
  );

  const handleWorkspaceChange = useCallback(
    (selectionList) => {
      options.current.workspaces = selectionList;

      getUsageReport();
    },
    [getUsageReport]
  );

  return (
    <>
      <Toolbar className={classes.Header}>
        <Box className={classes.JobInfoTitle}>
          <Typography className={classes.HeaderTitle} component="div">
            Job Engine Usage -
            <span>
              Running ({runningJobs}), Queued ({queuedJobs}), Scheduled ({scheduledJobs})
            </span>
          </Typography>
        </Box>
        <MultiSelectDropDown
          dataid="id"
          variant="standard"
          placeHolder={<MultiSelect title="Workspace" dataLength={options.current.workspaces.length} />}
          options={workspaceList}
          selected={options.current.workspaces}
          isSelectAll={true}
          disabled={loading}
          onSelectChange={handleWorkspaceChange}
          basedOn="label"
          searchOn="Name"
          allowSearching
        />
        <MultiSelectDropDown
          dataid="id"
          variant="standard"
          placeHolder={<MultiSelect title="Status" dataLength={options.current.selectedStatus.length} />}
          options={statusList}
          selected={options.current.selectedStatus}
          isSelectAll={true}
          disabled={loading}
          onSelectChange={handleStatusChange}
          basedOn="label"
        />
        <Box>
          <Tooltip placement="top" title="Refresh" arrow>
            <span>
              <IconButton disabled={loading} onClick={getUsageReport}>
                <RefreshOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Toolbar>

      <ThemeProvider theme={theme}>
        <Paper className={classes.DataTableContainer}>
          <KMKTable
            loading={loading}
            columns={reportColumns}
            containerHeight={tableHeight}
            noDataText={"No records found"}
            data={jobs}
            sortingProperties={{ order: options.current.order, orderBy: options.current.orderBy, handleSorting }}
            boldHeader
            tableProps={{ stickyHeader: true }}
            pagination={true}
            paginationProperties={{
              rowsPerPageOptions: [10, 25, 50, 100],
              count,
              rowsPerPage: options.current.limit,
              page: options.current.page,
              onPageChange: handleChangePage,
              handleChangeRowsPerPage: handleChangeRowsPerPage,
              component: "div",
              className: classes.pagination,
              "data-id": "StewardQueueTablePagination",
            }}
            dataIdKey="SageMakerReportID"
          />
        </Paper>
      </ThemeProvider>
    </>
  );
};

export default DetailTable;
